import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Wordmark } from "@/components/Logo";
import { Loader2, Eye, EyeOff, ArrowRight } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [quickAccounts, setQuickAccounts] = useState([]);

  useEffect(() => {
    api.get("/demo-accounts").then(({ data }) => setQuickAccounts(data.accounts || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  if (user) {
    return null;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) navigate("/", { replace: true });
    else setError(res.error);
  };

  const quickFill = (q) => {
    setEmail(q.email);
    setPassword(q.password);
    setError("");
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 relative bg-sidebar border-r border-sidebar-border/[0.06] overflow-hidden flex-col justify-between p-14">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(217,242,126,0.10),transparent_60%)]" />
        <div className="absolute inset-y-0 right-0 w-px bg-gradient-to-b from-transparent via-sidebar-primary/40 to-transparent" />
        <div className="relative flex items-center gap-3.5">
          <div className="leading-none">
            <p className="text-sidebar-foreground"><span className="sr-only">COMO</span><Wordmark className="h-8 w-auto text-sidebar-primary" aria-hidden="true" /></p>
            <p className="text-[10px] text-sidebar-muted tracking-[0.14em] uppercase mt-1.5">Bloom Dev Labs</p>
          </div>
        </div>
        <div className="relative max-w-md">
          <div className="rule-brass mb-8" />
          <h1 className="font-heading text-[2.75rem] text-sidebar-foreground leading-[1.1] mb-6">
            Every public conversation, kept in good order.
          </h1>
          <p className="text-sidebar-muted text-[15px] leading-relaxed">
            Comment moderation for public offices on Facebook and Instagram. Detection in English,
            Tagalog and Bisaya, a human review queue, and a complete audit trail.
          </p>
        </div>
        <p className="relative text-[11px] text-sidebar-muted tracking-wide">
          Aligned with the Philippine Data Privacy Act.
        </p>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm animate-fade-in-up">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <Wordmark className="h-6 w-auto text-slate-900" />
          </div>

          <h2 className="font-heading text-3xl text-slate-900 mb-2">Sign in</h2>
          <p className="text-sm text-slate-500 mb-7">Access your moderation workspace.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block eyebrow block mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                placeholder="you@agency.com"
                className="w-full px-4 py-2.5 rounded-lg border border-input bg-card text-sm text-foreground placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring transition"
              />
            </div>
            <div>
              <label className="block eyebrow block mb-2">Password</label>
              <div className="relative">
                <input
                  type={show ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  placeholder="••••••••"
                  className="w-full px-4 py-2.5 pr-11 rounded-lg border border-input bg-card text-sm text-foreground placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring transition"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password"
                >
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-sm tracking-wide transition disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          {quickAccounts.length > 0 && <div className="mt-8">
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Demo accounts</p>
            <div className="grid grid-cols-3 gap-2">
              {quickAccounts.map((q) => (
                <button
                  key={q.email}
                  onClick={() => quickFill(q)}
                  data-testid={`quick-${q.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-xs font-medium text-slate-600 bg-card border border-slate-200 rounded-lg py-2 px-2 hover:border-ring hover:text-foreground transition"
                >
                  {q.label}
                </button>
              ))}
            </div>
          </div>}
        </div>
      </div>
    </div>
  );
}
